// The till's view of its shop's printers, and of the prints that did not
// come out (UC-47, #367).
//
// The till's order store holds the printers and the print queue; this module
// only asks it and shows what it says. Every rule is the server's: a refusal
// is read by its code and mapped to a word the screen has a message for. The
// store keeps a job that did not print for an hour, and its list cannot say
// that a later job printed, so the retries the cashier made are kept here, in
// memory only: nothing goes to localStorage or sessionStorage.
//
// A print whose delivery is uncertain (#515) is held by the store until
// someone decides: the store lists it whatever its age. A held kitchen slip
// or bill makes its own notice, whose action is Decide rather than Print
// again, and every held job of this till, receipts and kicks included, is
// listed for Orders' "Waiting for a decision". A decision on its way is
// counted here, so the till holds still while one is.

import { SvelteMap, SvelteSet } from 'svelte/reactivity';
import { store } from '../api/client';
import type { components } from '../api/store';
import { problemOf } from './answer';
import {
	PRINT_COOLDOWN_MS,
	decidePrint,
	isHeld,
	queueBill,
	type DecideOutcome,
	type Decision,
	type PrintJob,
} from './printing';

export type Printer = components['schemas']['StorePrinterListBody']['printers'][number];
export type NewPrinter = components['schemas']['StoreNewPrinterBody'];
export type PrinterPatch = components['schemas']['StorePrinterChangeBody'];
type Purpose = Printer['purposes'][number];

const POLL_MS = 1000;
const WAIT_MS = 65_000;
const WATCH_MS = 3000;

let list = $state<Printer[]>([]);
let read = $state(false);

export function printersRead(): boolean {
	return read;
}
// The first successful read seeds the list. Afterwards, overlapping reads
// (a save's and the screen's) let only the latest one asked replace it.
let asked = 0;

/** The shop's printers as the store last read them; empty before the first read. */
export function printers(): Printer[] {
	return list;
}

/** Read the list. A failure keeps the list in hand. */
export async function loadPrinters(): Promise<void> {
	const ask = ++asked;
	try {
		const { data, error } = await store.GET('/store/printers');
		if (error === undefined && data) {
			if (ask === asked || !read) list = data.printers;
			read = true;
		}
	} catch {
		// No answer: the list in hand stays, and the next read decides.
	}
}

function printerFor(purpose: Purpose): Printer | null {
	return list.find((printer) => printer.purposes.includes(purpose)) ?? null;
}

/** The printer that prints receipts and bills, or null when none is set up. */
export function receiptPrinter(): Printer | null {
	return printerFor('receipts');
}

/** The printer that prints kitchen slips, or null when none is set up. */
export function kitchenPrinter(): Printer | null {
	return printerFor('kitchen');
}

export type RefusalCode =
	| 'name_in_use'
	| 'purpose_taken'
	| 'already_added'
	| 'invalid_name'
	| 'invalid_address'
	| 'invalid'
	| 'forbidden'
	| 'stale'
	| 'print_in_progress'
	| 'print_host_unavailable'
	| 'failed';

export interface Refusal {
	kind: 'refused';
	code: RefusalCode;
	/** The purpose another printer already prints, for purpose_taken. */
	purpose?: Purpose;
	/** The printer that holds the purpose or the address, when the server names it. */
	name?: string;
	/** The field a validation refusal names. */
	field?: string;
}

/** What the server's refusal says, in the words a screen has messages for. */
function refusalOf(status: number, error: unknown): Refusal {
	const problem = problemOf(error);
	const details: Record<string, unknown> = { ...problem?.details };
	const field = typeof details.path === 'string' ? details.path : undefined;
	const named = typeof details.name === 'string' ? details.name : undefined;
	if (status === 403 || problem?.code === 'FORBIDDEN')
		return { kind: 'refused', code: 'forbidden' };
	switch (problem?.code) {
		// The setup the screen read is not the store's any more: read it again.
		case 'PRINTER_CONFIG_STALE':
		case 'PRINTER_SETUP_NOT_LOCAL':
			return { kind: 'refused', code: 'stale' };
		case 'PRINT_IN_PROGRESS':
			return { kind: 'refused', code: 'print_in_progress' };
		// A Windows till's print host is starting (#524): it cannot look now.
		case 'PRINT_HOST_UNAVAILABLE':
			return { kind: 'refused', code: 'print_host_unavailable' };
		case 'PRINTER_NAME_IN_USE':
			return { kind: 'refused', code: 'name_in_use' };
		case 'PRINTER_PURPOSE_TAKEN': {
			const purpose = details.purpose;
			return {
				kind: 'refused',
				code: 'purpose_taken',
				...(purpose === 'receipts' || purpose === 'kitchen' ? { purpose } : {}),
				...(named === undefined ? {} : { name: named }),
			};
		}
		case 'PRINTER_ALREADY_ADDED':
			return {
				kind: 'refused',
				code: 'already_added',
				...(named === undefined ? {} : { name: named }),
			};
		case 'VALIDATION_ERROR': {
			const code =
				field === 'name' ? 'invalid_name' : field === 'address' ? 'invalid_address' : 'invalid';
			return { kind: 'refused', code, ...(field === undefined ? {} : { field }) };
		}
	}
	return { kind: 'refused', code: 'failed' };
}

export type SaveOutcome = { kind: 'saved' } | Refusal;

/** Add a printer. The server decides everything; only the name is trimmed. */
export async function addPrinter(input: NewPrinter): Promise<SaveOutcome> {
	try {
		const { error, response } = await store.POST('/store/printers', {
			body: { ...input, name: input.name.trim() },
		});
		if (error !== undefined) return refusalOf(response.status, error);
	} catch {
		return { kind: 'refused', code: 'failed' };
	}
	await loadPrinters();
	return { kind: 'saved' };
}

/** Change a printer. A new address sets it up again. */
export async function changePrinter(id: string, patch: PrinterPatch): Promise<SaveOutcome> {
	try {
		const { error, response } = await store.PATCH('/store/printers/{printerId}', {
			params: { path: { printerId: id } },
			body: patch.name === undefined ? patch : { ...patch, name: patch.name.trim() },
		});
		if (error !== undefined) return refusalOf(response.status, error);
	} catch {
		return { kind: 'refused', code: 'failed' };
	}
	await loadPrinters();
	return { kind: 'saved' };
}

export type SlipOutcome = { kind: 'queued'; job: PrintJob } | Refusal;

let slipPending: { key: string; answer: Promise<SlipOutcome> } | null = null;
let slipSent: { key: string; at: number; outcome: SlipOutcome } | null = null;

/**
 * Print a test slip at a typed address. A second tap while the first is on
 * its way, or right after it queued, gets the first one's answer instead of
 * another slip.
 */
export function requestTestSlip(address: string): Promise<SlipOutcome> {
	return oneSlip(`address:${address}`, () =>
		store.POST('/store/printers/test-slip', {
			body: { address },
			params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
		}),
	);
}

/**
 * Print a test slip on a printer of the till's own setup, or on a printer
 * `findPrinter` found. A second tap answers as `requestTestSlip` does.
 */
export function requestMeshTestSlip(printerId: string): Promise<SlipOutcome> {
	return oneSlip(`printer:${printerId}`, () =>
		store.POST('/store/printer-mesh/test-slip', {
			body: { printerId },
			params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
		}),
	);
}

type SlipPost = () => Promise<{ data?: PrintJob; error?: unknown; response: Response }>;

function oneSlip(key: string, post: SlipPost): Promise<SlipOutcome> {
	if (slipPending?.key === key) return slipPending.answer;
	if (slipSent?.key === key && Date.now() - slipSent.at < PRINT_COOLDOWN_MS) {
		return Promise.resolve(slipSent.outcome);
	}
	const answer = postTestSlip(key, post, retries.signal).finally(() => {
		if (slipPending?.answer === answer) slipPending = null;
	});
	slipPending = { key, answer };
	return answer;
}

async function postTestSlip(
	key: string,
	post: SlipPost,
	signal: AbortSignal,
): Promise<SlipOutcome> {
	let outcome: SlipOutcome;
	try {
		const { data, error, response } = await post();
		outcome =
			error === undefined && data
				? { kind: 'queued', job: data }
				: refusalOf(response.status, error);
	} catch {
		outcome = { kind: 'refused', code: 'failed' };
	}
	if (!signal.aborted && outcome.kind === 'queued') slipSent = { key, at: Date.now(), outcome };
	return outcome;
}

// A till's own printer setup (#522). The store's mesh says whether the till
// prints with the shop's list from Bosla or with its own setup, what the app
// can do, and the setup's revision. An older store has no mesh and answers
// 404: the screen then works exactly as before. `receiptPrinter()` and the
// print notices keep reading `/store/printers`, which lists the till's own
// printers once its setup is on.

export type PrinterMesh = components['schemas']['PrinterMeshBody'];
export type FoundPrinter = components['schemas']['CandidatesBody']['candidates'][number];
type SetupPrinter = components['schemas']['PrinterConfigBody']['printers'][number];

let mesh = $state<PrinterMesh | null>(null);
let meshAsked = 0;

/** "shop", "till", or a later value; null before a read and on an older store. */
export function meshSetup(): string | null {
	return mesh?.setup ?? null;
}

/** What this app can do, such as local_setup; empty before a read and on an older store. */
export function meshCapabilities(): string[] {
	return mesh?.capabilities ?? [];
}

export type MeshSharing = NonNullable<PrinterMesh['sharing']>;

/**
 * How the till shares its own setup with the shop's other tills (#517):
 * null before a read, on the shop's list, on an older store, and on a store
 * that shares nothing.
 */
export function meshSharing(): MeshSharing | null {
	return mesh?.sharing ?? null;
}

/** The till's own setup's revision as last read; 0 while the till uses the shop's list. */
export function meshRevision(): number {
	return mesh?.revision ?? 0;
}

/**
 * A Windows till's print host companion (#524): "ready", "starting", or null
 * where the print host runs in the store, or on an older store.
 */
export function meshPrintHost(): string | null {
	return mesh?.printHost ?? null;
}

export type MeshPrinter = PrinterMesh['printers'][number];
export type CouldBePrinter = NonNullable<MeshPrinter['couldBe']>[number];

/**
 * Whether this app finds a printer of the till's own setup again after it
 * moves (UC-49, #518): the find-again parts show only when it is listed.
 */
export function canFindAgain(): boolean {
	return mesh?.setup === 'till' && mesh.capabilities.includes('find_again');
}

/** The ids of the printers that need identifying: the till cannot tell them from another. */
export function identifyingIds(): string[] {
	if (!canFindAgain() || !mesh) return [];
	return mesh.printers.filter((p) => p.binding?.status === 'unresolved').map((p) => p.id);
}

/** The found printers that could be this one, for a printer that needs identifying. */
export function couldBeFor(id: string): CouldBePrinter[] {
	return mesh?.printers.find((p) => p.id === id)?.couldBe ?? [];
}

/** Say which found printer a printer that needs identifying is: a manager's save. */
export function identifyPrinter(id: string, candidateId: string): Promise<SaveOutcome> {
	return saveLocalSetup({ kind: 'change', id, candidateId });
}

/** Read the mesh. 404 is an older store with none; another failure keeps the mesh in hand. */
export async function loadMesh(): Promise<void> {
	const ask = ++meshAsked;
	try {
		const { data, error, response } = await store.GET('/store/printer-mesh');
		if (ask !== meshAsked) return;
		if (error === undefined && data) mesh = data;
		else if (response.status === 404) mesh = null;
	} catch {
		// No answer: the mesh in hand stays, and the next read decides.
	}
}

/** A save's or a turn-on's answer is the mesh: it wins over a read asked before it. */
function keepMesh(read: PrinterMesh): void {
	meshAsked++;
	mesh = read;
}

/** Turn the till's own setup on, then read the mesh and the list again. */
export async function activateLocalSetup(): Promise<SaveOutcome> {
	try {
		const { data, error, response } = await store.POST('/store/printer-mesh/activate');
		if (error !== undefined || !data) return refusalOf(response.status, error);
		keepMesh(data);
	} catch {
		return { kind: 'refused', code: 'failed' };
	}
	await Promise.all([loadMesh(), loadPrinters()]);
	return { kind: 'saved' };
}

export type FindOutcome = { kind: 'found'; printer: FoundPrinter } | Refusal;

/** Find the printer that answers at a typed address. */
export async function findPrinter(address: string): Promise<FindOutcome> {
	try {
		const { data, error, response } = await store.POST('/store/printer-mesh/discover', {
			body: { address },
		});
		if (error !== undefined || !data) return refusalOf(response.status, error);
		const found = data.candidates[0];
		// The store refuses an address no printer answers at; an empty list says the same.
		return found
			? { kind: 'found', printer: found }
			: { kind: 'refused', code: 'invalid_address', field: 'address' };
	} catch {
		return { kind: 'refused', code: 'failed' };
	}
}

/** The kind of a USB printer plugged into an Android till: the store's `android_usb`. */
export const ANDROID_USB = 'android_usb';

/** The fields of one printer of the till's own setup, as a manager sets them. */
export interface SetupFields {
	name: string;
	purposes: Purpose[];
	paperWidthMm: number;
	hasDrawer: boolean;
}

/**
 * One change to the till's own setup: a new printer set up on a found one,
 * or a printer changed in place, set up again on a found printer when the
 * change carries its `candidateId`.
 */
export type SetupChange =
	| ({ kind: 'add'; candidateId: string } & SetupFields)
	| ({ kind: 'change'; id: string; candidateId?: string } & Partial<SetupFields>);

function changed(p: SetupPrinter, change: SetupChange & { kind: 'change' }): SetupPrinter {
	const out: SetupPrinter = {
		...p,
		name: (change.name ?? p.name).trim(),
		purposes: change.purposes ?? p.purposes,
		paperWidthMm: change.paperWidthMm ?? p.paperWidthMm,
		hasDrawer: change.hasDrawer ?? p.hasDrawer,
	};
	if (change.candidateId !== undefined) out.candidateId = change.candidateId;
	return out;
}

/**
 * Save the whole setup with one change: every printer of the mesh last read
 * with its fields, the change applied, based on the revision read. A save
 * and a stale refusal both read the mesh and the list again.
 */
export async function saveLocalSetup(change: SetupChange): Promise<SaveOutcome> {
	const read = mesh;
	if (read === null) return { kind: 'refused', code: 'stale' };
	const printers: SetupPrinter[] = read.printers.map((p) => {
		const kept: SetupPrinter = {
			id: p.id,
			name: p.name,
			purposes: p.purposes,
			paperWidthMm: p.paperWidthMm,
			hasDrawer: p.hasDrawer,
		};
		return change.kind === 'change' && change.id === p.id ? changed(kept, change) : kept;
	});
	if (change.kind === 'add') {
		printers.push({
			candidateId: change.candidateId,
			name: change.name.trim(),
			purposes: change.purposes,
			paperWidthMm: change.paperWidthMm,
			hasDrawer: change.hasDrawer,
		});
	}
	let outcome: SaveOutcome;
	try {
		const { data, error, response } = await store.PUT('/store/printer-mesh/config', {
			body: { baseRevision: read.revision, printers },
		});
		if (error === undefined && data) {
			keepMesh(data);
			outcome = { kind: 'saved' };
		} else {
			outcome = refusalOf(response.status, error);
		}
	} catch {
		return { kind: 'refused', code: 'failed' };
	}
	if (outcome.kind === 'saved' || outcome.code === 'stale') {
		await Promise.all([loadMesh(), loadPrinters()]);
	}
	return outcome;
}

// A printer plugged into the till, and sharing it (#523, #516, #622). On a
// Windows till it is a USB printer on a Windows print queue, on an Android
// till a USB printer Android lets Bosla use; the screen shows both as one
// attached kind, in one picker and one row line. The store lists them among
// the printers it finds, and says how the one it shares on port 9100 stands.
// A screen shows these only when the app lists windows_usb, android_usb or
// raw_export, and takes a share state it does not know as off.

/** The binding kind of a printer plugged into a Windows till. */
export const USB_KIND = 'windows_queue';

/** The capabilities of an app that finds the printers plugged into its till. */
const ATTACHED_CAPABILITIES = ['windows_usb', ANDROID_USB];

/** Whether a binding or found printer of this kind is plugged into the till. */
export function isAttached(kind: string): boolean {
	return kind === USB_KIND || kind === ANDROID_USB;
}

/** Whether the app lists a way to find the printers plugged into its till. */
export function findsAttached(capabilities: string[]): boolean {
	return ATTACHED_CAPABILITIES.some((c) => capabilities.includes(c));
}

/**
 * The name a printer plugged into the till goes by: the Windows queue's name,
 * or the model an Android printer reports; empty when it has none.
 */
export function attachedName(place: {
	kind: string;
	locator: string;
	evidence?: Record<string, string>;
}): string {
	if (place.kind === USB_KIND) return place.locator;
	return (place.evidence?.model ?? '').trim();
}

export type SharedPrinter = NonNullable<PrinterMesh['sharedPrinter']>;

/** How the shared printer stands; null where the app cannot share one. */
export function meshShared(): SharedPrinter | null {
	return mesh?.sharedPrinter ?? null;
}

/**
 * The name of the printer plugged into the till that a printer of the setup
 * is bound to (empty when it has none), or null when it is bound to none.
 */
export function meshAttachedName(printerId: string): string | null {
	const binding = mesh?.printers.find((p) => p.id === printerId)?.binding;
	return binding && isAttached(binding.kind) ? attachedName(binding) : null;
}

export type AttachedOutcome = { kind: 'found'; printers: FoundPrinter[] } | Refusal;

/**
 * Find the printers plugged into this till, of either kind: with no address,
 * the store asks only them. Only an app that lists windows_usb or
 * android_usb, on a till with its own setup, has any: otherwise nothing is
 * asked and the list is empty.
 */
export async function findAttachedPrinters(): Promise<AttachedOutcome> {
	if (meshSetup() !== 'till' || !findsAttached(meshCapabilities())) {
		return { kind: 'found', printers: [] };
	}
	try {
		const { data, error, response } = await store.POST('/store/printer-mesh/discover', {
			body: {},
		});
		if (error !== undefined || !data) return refusalOf(response.status, error);
		return { kind: 'found', printers: data.candidates.filter((c) => isAttached(c.kind)) };
	} catch {
		return { kind: 'refused', code: 'failed' };
	}
}

/**
 * Share one printer plugged into this till on the shop's network, or stop
 * sharing with an empty id: the whole setup as last read, unchanged, with
 * the printer it shares, based on the revision read.
 */
export async function shareLocalPrinter(printerId: string): Promise<SaveOutcome> {
	const read = mesh;
	if (read === null) return { kind: 'refused', code: 'stale' };
	const printers: SetupPrinter[] = read.printers.map((p) => ({
		id: p.id,
		name: p.name,
		purposes: p.purposes,
		paperWidthMm: p.paperWidthMm,
		hasDrawer: p.hasDrawer,
	}));
	let outcome: SaveOutcome;
	try {
		const { data, error, response } = await store.PUT('/store/printer-mesh/config', {
			body: { baseRevision: read.revision, printers, exportedSink: printerId },
		});
		if (error === undefined && data) {
			keepMesh(data);
			outcome = { kind: 'saved' };
		} else {
			outcome = refusalOf(response.status, error);
		}
	} catch {
		return { kind: 'refused', code: 'failed' };
	}
	if (outcome.kind === 'refused' && outcome.code === 'stale') await loadMesh();
	return outcome;
}

export type JobResult =
	{ kind: 'printed' } | { kind: 'failed'; failReason?: string } | { kind: 'stopped' };

async function readJobs(): Promise<PrintJob[] | null> {
	try {
		const { data, error } = await store.GET('/store/print-jobs');
		return error === undefined && data ? data.jobs : null;
	} catch {
		return null;
	}
}

/**
 * Watch one job. Printed jobs leave the store's list, so a job that is not in
 * it has printed. Polls every second for the supplied wait (65 seconds by
 * default), even when a store call never answers; the signal cancels the wait.
 */
export async function watchJob(
	jobId: string,
	signal: AbortSignal,
	waitMs = WAIT_MS,
): Promise<JobResult> {
	if (signal.aborted) return { kind: 'stopped' };
	let stopped = false;
	let pollTimer: ReturnType<typeof setTimeout> | undefined;
	let wake: (() => void) | undefined;
	let finish!: (result: JobResult) => void;
	const ended = new Promise<JobResult>((resolve) => (finish = resolve));
	const abort = () => finish({ kind: 'stopped' });
	signal.addEventListener('abort', abort, { once: true });
	const deadline = setTimeout(() => finish({ kind: 'failed' }), waitMs);
	async function poll(): Promise<JobResult> {
		while (!stopped) {
			const jobs = await readJobs();
			if (stopped) return { kind: 'stopped' };
			// No answer says nothing about the paper: keep watching until the deadline.
			if (jobs !== null) {
				const job = jobs.find((j) => j.id === jobId);
				if (!job) return { kind: 'printed' };
				if (job.state === 'printed') return { kind: 'printed' };
				if (job.state === 'failed') {
					return { kind: 'failed', ...(job.failReason ? { failReason: job.failReason } : {}) };
				}
			}
			await new Promise<void>((resolve) => {
				wake = resolve;
				pollTimer = setTimeout(resolve, POLL_MS);
			});
		}
		return { kind: 'stopped' };
	}
	try {
		return await Promise.race([poll(), ended]);
	} finally {
		stopped = true;
		clearTimeout(deadline);
		clearTimeout(pollTimer);
		wake?.();
		signal.removeEventListener('abort', abort);
	}
}

/**
 * A print that did not come out and the cashier can print again (`failed`),
 * or one the store holds because its delivery is uncertain, which waits for
 * someone to decide (`held`, #515).
 */
export interface PrintNotice {
	kind: 'failed' | 'held';
	/**
	 * A failed print's is one per ticket or order: `kitchen_slip:<ticketId>` or
	 * `bill:<orderId>`. A held one's is one per job: `held:<jobId>`.
	 */
	key: string;
	document: 'kitchen_slip' | 'bill';
	/** The held job, which its decision names. */
	jobId?: string;
	ticketId?: string;
	orderId?: string;
}

let jobs = $state<PrintJob[]>([]);
let clock = $state(Date.now());
// Retries that printed, by notice key. The store's list cannot say so.
const reprintDone = new SvelteMap<string, number>();

function reprinted(job: PrintJob, key: string): boolean {
	const at = reprintDone.get(key);
	return at !== undefined && Date.parse(job.createdAt) <= at;
}

function noticeKey(job: PrintJob): string | null {
	if (job.document === 'kitchen_slip' && job.ticketId) return `kitchen_slip:${job.ticketId}`;
	if (job.document === 'bill' && job.orderId) return `bill:${job.orderId}`;
	return null;
}

const notices = $derived.by(() => {
	const live = new SvelteSet<string>();
	for (const job of jobs) {
		const key = noticeKey(job);
		if (key !== null && (job.state === 'queued' || job.state === 'printing')) live.add(key);
	}
	const seen = new SvelteSet<string>();
	const out: PrintNotice[] = [];
	for (const job of jobs) {
		const key = noticeKey(job);
		if (key === null || job.state !== 'failed') continue;
		const doc = job.document === 'bill' ? 'bill' : 'kitchen_slip';
		const ids = {
			...(job.ticketId ? { ticketId: job.ticketId } : {}),
			...(job.orderId ? { orderId: job.orderId } : {}),
		};
		// A job whose delivery is uncertain is never a plain failure: held, it
		// waits for a decision; decided, the decision settled it.
		if (job.delivery === 'uncertain') {
			if (isHeld(job)) {
				out.push({ kind: 'held', key: `held:${job.id}`, document: doc, jobId: job.id, ...ids });
			}
			continue;
		}
		if (live.has(key) || seen.has(key) || reprinted(job, key)) continue;
		seen.add(key);
		out.push({ kind: 'failed', key, document: doc, ...ids });
	}
	return out;
});

/**
 * The prints that failed and have not been retried, one per ticket or order,
 * and the kitchen slips and bills held for a decision, one per job.
 */
export function printNotices(): PrintNotice[] {
	return notices;
}

const notAnswering = $derived.by(() => {
	const liveDocs = new SvelteSet<string>();
	for (const job of jobs) {
		if (job.state === 'queued' || job.state === 'printing') {
			liveDocs.add(job.document);
		}
	}
	const purposes = new SvelteSet<Purpose>();
	for (const job of jobs) {
		if (job.state !== 'failed' || clock - Date.parse(job.createdAt) > 10 * 60_000) continue;
		if (liveDocs.has(job.document)) continue;
		if (job.document === 'kitchen_slip') {
			// A slip printed again leaves its failed job in the store's list for an hour.
			if (job.ticketId && reprinted(job, `kitchen_slip:${job.ticketId}`)) continue;
			purposes.add('kitchen');
		} else if (job.document === 'receipt' || job.document === 'bill' || job.document === 'drawer') {
			const key = noticeKey(job);
			if (key && reprinted(job, key)) continue;
			purposes.add('receipts');
		}
	}
	return Array.from(purposes);
});

/**
 * Purposes with a failed job created within the last ten minutes. Test jobs
 * are ignored, as are documents currently queued/printing and retries known
 * to have printed here. The store's list cannot reveal later printed jobs;
 * slip notices remain until reprinted regardless of this time window.
 */
export function notAnsweringPurposes(): Purpose[] {
	return notAnswering;
}

let watching = 0;
let running = false;
let watchTimer: ReturnType<typeof setTimeout> | undefined;
let retries = new AbortController();
const inFlight = new SvelteSet<string>();

export function printNoticeBusy(key: string): boolean {
	return inFlight.has(key);
}

async function pollJobs(round: number): Promise<void> {
	const read = await readJobs();
	if (round !== watching) return;
	clock = Date.now();
	if (read !== null) jobs = read;
	watchTimer = setTimeout(() => void pollJobs(round), WATCH_MS);
}

/** Poll the store's print jobs every 3 seconds while the till is signed in. */
export function startPrintWatch(): void {
	if (running) return;
	running = true;
	void pollJobs(++watching);
}

/**
 * Stop polling and forget the jobs read. A lock stops it too, so what the
 * session knows of its retries stays: a slip printed again must not come back
 * as unprinted when the till unlocks. Sign-out forgets that (`forgetPrinting`).
 */
export function stopPrintWatch(): void {
	running = false;
	watching++;
	clearTimeout(watchTimer);
	watchTimer = undefined;
	jobs = [];
}

/** Sign-out: stop watching retries and forget which were printed and what is in flight. */
export function forgetPrinting(): void {
	retries.abort();
	retries = new AbortController();
	reprintDone.clear();
	inFlight.clear();
	deciding.clear();
	slipPending = null;
	slipSent = null;
}

const held = $derived(jobs.filter((job) => isHeld(job) && job.document !== 'test'));

/**
 * Every job of this till the store holds for a decision, whatever its
 * document and its age, in the store's order; refreshed as the notices are.
 */
export function heldJobs(): PrintJob[] {
	return held;
}

// The decisions on their way, by held job: a second tap while one is gets
// its answer instead of sending another.
const deciding = new SvelteMap<string, Promise<DecideOutcome | null>>();

/** Whether any decision is on its way: the till holds still while one is (rest.ts). */
export function decisionsPending(): boolean {
	return deciding.size > 0;
}

/**
 * Decide a held job (#515), then read the list again so the notice and the
 * held row leave as soon as the store says. Returns what decidePrint does.
 */
export function decideHeld(jobId: string, decision: Decision): Promise<DecideOutcome | null> {
	const pending = deciding.get(jobId);
	if (pending) return pending;
	const answer = (async () => {
		const outcome = await decidePrint(jobId, decision);
		if (outcome !== null) await refreshPrintJobs();
		return outcome;
	})().finally(() => {
		if (deciding.get(jobId) === answer) deciding.delete(jobId);
	});
	deciding.set(jobId, answer);
	return answer;
}

/** Read the store's jobs now, while the watch runs, rather than at its next turn. */
export async function refreshPrintJobs(): Promise<void> {
	if (!running) return;
	const round = watching;
	const read = await readJobs();
	if (round === watching && running && read !== null) {
		clock = Date.now();
		jobs = read;
	}
}

/**
 * Queue a retry once per in-flight notice request, then watch its new job.
 * `onPrinted` runs once the copy printed; an error it throws is dropped.
 */
export async function printNoticeAgain(
	notice: PrintNotice,
	onPrinted?: () => void | Promise<void>,
): Promise<boolean> {
	if (inFlight.has(notice.key)) return false;
	if (notice.kind !== 'failed') return false;
	if (notice.document === 'kitchen_slip' ? !notice.ticketId : !notice.orderId) return false;
	inFlight.add(notice.key);
	const signal = retries.signal;
	const round = watching;
	let job: PrintJob | null;
	try {
		if (notice.document === 'bill') {
			job = await queueBill(notice.orderId!);
		} else {
			const { data, error } = await store.POST('/store/print-jobs', {
				body: { document: 'kitchen_slip', ticketId: notice.ticketId! },
				params: { header: { 'Idempotency-Key': crypto.randomUUID() } },
			});
			job = error === undefined && data ? data : null;
		}
	} catch {
		job = null;
	} finally {
		if (!signal.aborted) inFlight.delete(notice.key);
	}
	if (!job || signal.aborted) return false;
	void watchJob(job.id, signal, notice.document === 'kitchen_slip' ? 305_000 : 65_000).then(
		async (result) => {
			if (!signal.aborted && result.kind === 'printed') {
				// The store's own clock, as every job's createdAt is: the till's wall clock may differ.
				reprintDone.set(notice.key, Date.parse(job.createdAt));
				try {
					await onPrinted?.();
				} catch {
					// The copy printed; a caller's refresh that failed leaves no one to tell.
				}
			}
		},
	);
	if (running && round === watching) void refreshPrintJobs();
	return true;
}
